import { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './OrganizationLandscapesPage.module.css';
import { OrganizationLandscapesTemplate } from '../../templates';
import { LandscapeBuyerRelationType } from '../../../store/landscapeAPI/landscape/landscape-buyer-relation-type';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  deleteCompactSupporter,
  fetchCompactSupportersByOrganizationId,
  saveCompactSupporter,
} from '../../../store/tradeAPI/commodity/reducers/landscape-supporters.slice';
import { useParams } from 'react-router-dom';
import { fetchLandscapes } from '../../../store/landscapeAPI/landscape/reducers/landscape.slice';
import { LandscapeSupportersResponseDto } from '../../../store/tradeAPI/commodity/dto/response/landscape-suporters-response.dto';
import { LandscapeResponseDto } from '../../../store/landscapeAPI/landscape/dto/response/landscape-response.dto';
import Spinner from '../../atoms/Spinner/Spinner';
import { LandscapeSupportersSaveRequestDto } from '../../../store/tradeAPI/commodity/dto/request/landscape-suporters-save-request.dto';
import { DEFAULT_CONTEXT_ID, getUUID } from '../../../utils/UUID';
import { AddOrganizationLandscapeModal } from '../../organisms';

interface OrganizationLandscapesPageProps {}

interface TableItem {
  id: string;
  landscapeName: string;
  relationType: LandscapeBuyerRelationType;
}

const OrganizationLandscapesPage: FC<OrganizationLandscapesPageProps> = () => {
  const { organizationId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [data, setData] = useState<TableItem[]>();
  const [supporter, setSupporter] = useState<LandscapeSupportersSaveRequestDto>(
    {
      organizationId: organizationId ?? '',
      compactId: '',
      commodityId: DEFAULT_CONTEXT_ID,
      relationType: LandscapeBuyerRelationType.Funders,
      id: getUUID(),
      published: true,
    }
  );

  const { landscapes, status: landscapeStatus } = useSelector(
    (state: RootState) => state.landscapeSlice
  );
  const { compactSupporters: supporters, status: supporterStatus } =
    useSelector((state: RootState) => state.landscapeSupportersSlice);

  useEffect(() => {
    dispatch(fetchLandscapes());
    dispatch(fetchCompactSupportersByOrganizationId(organizationId!));
  }, [dispatch, organizationId]);

  useEffect(() => {
    const fetchData = async (
      supporters: LandscapeSupportersResponseDto[],
      landscapes: LandscapeResponseDto[]
    ) => {
      const filteredTableItems: TableItem[] = supporters.map((supporter) => {
        const matchingLandscape = landscapes.find(
          (landscape) => landscape.id === supporter.compactId
        );

        return {
          id: supporter.id,
          landscapeName: matchingLandscape?.name ?? '',
          relationType: supporter.relationType,
        };
      });

      setData(filteredTableItems);
    };
    if (supporters && landscapes) {
      fetchData(supporters, landscapes);
    }
  }, [supporters, landscapes, dispatch]);

  const handleOnChange = (supporter: LandscapeSupportersSaveRequestDto) => {
    setSupporter(supporter);
  };

  const handleDelete = async (id: string) => {
    await dispatch(deleteCompactSupporter(id));
    dispatch(fetchCompactSupportersByOrganizationId(organizationId!));
  };

  const callbackFn = async (supporter: LandscapeSupportersSaveRequestDto) => {
    await dispatch(saveCompactSupporter(supporter));
    dispatch(fetchCompactSupportersByOrganizationId(organizationId!));
    setShowModal(false);
  };

  if (landscapeStatus === 'loading' || supporterStatus === 'loading') {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      {showModal && (
        <AddOrganizationLandscapeModal
          show={showModal}
          setShow={setShowModal}
          landscapes={landscapes ?? []}
          handleOnChange={handleOnChange}
          callbackFn={callbackFn}
          landscapeSupporter={supporter}
        />
      )}
      <OrganizationLandscapesTemplate
        setShow={setShowModal}
        data={data ?? []}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default OrganizationLandscapesPage;
